import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import type { Resolver } from "react-hook-form";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

import { MUTATION_UPDATE_DEBT_CONSOLIDATION } from "api/mutations/application";
import { QUERY_APPLICATION_DEBT_CONSOLIDATION } from "api/queries/application";

import { yupResolver } from "@hookform/resolvers/yup";
import AlertError from "components/Alert/Error";
import CTAButton from "components/CTA/Button";
import Loading from "components/Loading";
import PageBreadcrumb from "components/Page/Breadcrumb";
import PageContent from "components/Page/Content";
import usePageTitle from "hooks/usePageTitle";
import { getCaseDetailUrl } from "routes/links";
import { APP_URLS } from "settings";
import CashOutSection from "./components/CashOut";
import ConsolidatedCreditTable from "./components/ConsolidatedCreditTable";
import CustomerBankDetailsSection from "./components/CustomerBankDetails";
import FormOverLimit from "./components/Overlimt";
import StateContainer from "./components/StateContainer";
import FormSuccess from "./components/Success";
import FormSummary from "./components/Summary";
import type { ConsolidationItem, DebtConsolidationFormValues } from "./types";
import {
  getCashOutItemsTotal,
  getDefaultSettlePurpose,
  getLeftOverCashPurpose,
  getTotalCashOut,
  parseCreditProvidersForApi,
} from "./utils";
import { createDebtConsolidationValidationSchema } from "./validation";
import FormErrors from "./components/FormErrors";
import { PAID_TO_CREDITOR } from "./consts";

const DEFAULT_CREDIT_PROVIDERS: ConsolidationItem[] = [];

export default function DebtConsolidationForm() {
  const [saved, setSaved] = useState<boolean>(false);

  const { caseId: urlCaseId, applicationId: urlApplicationId } = useParams<{
    caseId: string;
    applicationId: string;
  }>();

  const {
    data,
    loading: getDebtConsolidationLoading,
    error: getDebtConsolidationError,
  } = useQuery(QUERY_APPLICATION_DEBT_CONSOLIDATION, {
    variables: {
      applicationUuid: urlApplicationId,
    },
    fetchPolicy: "no-cache",
  });

  const [
    updateDebtConsolidation,
    {
      loading: updateDebtConsolidationLoading,
      error: updateDebtConsolidationError,
      data: updateDebtConsolidationData,
    },
  ] = useMutation(MUTATION_UPDATE_DEBT_CONSOLIDATION);

  const hasDebtsToSettle =
    data?.data?.debtConsolidation?.application?.hasDebtsToSettle;

  const loanAmount = data?.data?.debtConsolidation?.loanAmount ?? 0;

  const debtConsolidationValidationSchema = useMemo(
    () => createDebtConsolidationValidationSchema(loanAmount),
    [loanAmount],
  );

  const {
    control,
    register,
    reset,
    getValues,
    setValue,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm<DebtConsolidationFormValues>({
    defaultValues: {
      creditProviders: DEFAULT_CREDIT_PROVIDERS,
      cashOuts: {},
      cashOutPurpose: "",
      cashOutAccountNumber: "",
      cashOutSortCode: "",
      cashOutApplicantUuid: "",
    },
    resolver: yupResolver(
      debtConsolidationValidationSchema,
    ) as unknown as Resolver<DebtConsolidationFormValues>,
    mode: "onChange",
  });

  const { fields } = useFieldArray({
    control,
    name: "creditProviders",
  });

  const creditProviders = useWatch({ control, name: "creditProviders" });
  const cashOutPurpose = useWatch({ control, name: "cashOutPurpose" });

  const consolidatedItems = creditProviders.filter(
    (c) => c.paidTo === PAID_TO_CREDITOR,
  );
  const totalConsolidated = consolidatedItems.reduce(
    (s, c) => s + Number(c.settlementAmount || 0),
    0,
  );

  const totalCashOut = getTotalCashOut(creditProviders, loanAmount);
  const remainingCash = totalCashOut - getCashOutItemsTotal(creditProviders);
  const overLimit =
    parseFloat((totalConsolidated + totalCashOut).toFixed(2)) > loanAmount;

  const applicants = data?.data?.debtConsolidation?.application?.applicants;
  const debtConsolidationUuid = data?.data?.debtConsolidation?.uuid ?? "";
  const applicationUuid =
    data?.data?.debtConsolidation?.application?.uuid ?? "";
  const caseReference =
    data?.data?.debtConsolidation?.application?.case?.reference ?? "";
  usePageTitle(caseReference && `Debt consolidation - Case ${caseReference}`);

  const stateOk = !getDebtConsolidationLoading && !getDebtConsolidationError;

  const onSubmit = async () => {
    clearErrors();

    if (overLimit) {
      window.scrollTo(0, 0);
      return;
    }

    const isValid = await trigger();

    if (!isValid) {
      window.scrollTo(0, 0);
      return;
    }

    const data = getValues();

    updateDebtConsolidation({
      variables: {
        applicationUuid,
        data: {
          uuid: debtConsolidationUuid,
          cashOutPurpose: getLeftOverCashPurpose(data.cashOuts),
          cashOutAccountNumber: data.cashOutAccountNumber ?? "",
          cashOutSortCode: data.cashOutSortCode,
          cashOutApplicantUuid:
            data.cashOutApplicantUuid === "" ? null : data.cashOutApplicantUuid,
          creditors: parseCreditProvidersForApi(
            data.creditProviders,
            data.cashOuts,
          ),
        },
      },
    });
  };

  useEffect(() => {
    if (updateDebtConsolidationData) {
      window.scrollTo(0, 0);
      setSaved(true);
    }
  }, [updateDebtConsolidationData]);

  /**
   * Reset the form values when API data is loaded
   */
  useEffect(() => {
    if (data) {
      const debtConsolidation = data.data.debtConsolidation;
      const disbursement = debtConsolidation?.disbursementBankAccount;
      reset({
        uuid: debtConsolidation?.uuid,
        creditProviders: debtConsolidation?.creditors ?? [],
        cashOutAccountNumber:
          debtConsolidation?.cashOutAccountNumber ||
          disbursement?.accountNumber ||
          "",
        cashOutSortCode:
          debtConsolidation?.cashOutSortCode || disbursement?.sortCode || "",
        cashOutApplicantUuid:
          debtConsolidation?.cashOutApplicant?.uuid ??
          disbursement?.applicantUuid,
        cashOutPurpose: data.data.debtConsolidation?.cashOutPurpose ?? "",
        cashOuts: {
          ...data.data.debtConsolidation?.creditors?.reduce(
            (acc: Record<string, string>, c: ConsolidationItem) => {
              const stored = c.purpose?.trim();
              acc[`settle-${c.uuid}`] = stored || getDefaultSettlePurpose(c);
              return acc;
            },
            {} as Record<string, string>,
          ),
          remaining: data.data.debtConsolidation?.cashOutPurpose ?? "",
        },
      });
    }
  }, [data, reset]);

  /**
   * Handle submission of the form
   */
  if (saved) {
    return <FormSuccess />;
  }

  return (
    <div>
      <PageBreadcrumb
        items={[
          { label: "Cases", to: APP_URLS.CASE_MANAGEMENT },
          { label: caseReference, to: getCaseDetailUrl(urlCaseId ?? "") },
          { label: "Debt Consolidation Form" },
        ]}
      />

      <PageContent padding={false}>
        <>
          {getDebtConsolidationLoading && (
            <StateContainer>
              <Loading />
              <p className="mt-4">Loading Debt Consolidation Form...</p>
            </StateContainer>
          )}

          {getDebtConsolidationError && (
            <StateContainer>
              <p>
                Sorry, there has been an error loading your Debt Consolidation
                Form.
              </p>
              <p className="mt-2">Please contact us to discuss this further.</p>
            </StateContainer>
          )}

          {stateOk && (
            <form
              className="space-y-8"
              onSubmit={(event) => event.preventDefault()}
              noValidate
            >
              <FormSummary
                netLoanAmount={loanAmount}
                totalConsolidated={totalConsolidated}
                totalCashOut={totalCashOut}
                overLimit={overLimit}
              />

              <FormOverLimit
                overLimit={overLimit}
                totalConsolidated={totalConsolidated + totalCashOut}
                netLoanAmount={loanAmount}
              />

              <FormErrors errors={errors} />

              {
                // Only captura creditors and cash out details if we have debts to settle
                hasDebtsToSettle && (
                  <>
                    <ConsolidatedCreditTable
                      fields={fields}
                      creditProviders={creditProviders}
                      control={control}
                      register={register}
                      errors={errors}
                    />

                    <CashOutSection
                      control={control}
                      getValues={getValues}
                      setValue={setValue}
                      creditProviders={creditProviders}
                      loanAmount={loanAmount}
                      totalConsolidated={totalConsolidated}
                      remainingCash={remainingCash}
                      leftOverCashPurpose={cashOutPurpose}
                    />
                  </>
                )
              }

              <CustomerBankDetailsSection
                control={control}
                register={register}
                applicants={applicants}
                errors={errors}
              />

              {updateDebtConsolidationError && (
                <AlertError title="Error saving Debt Consolidation Form">
                  <>
                    <p>
                      Sorry, there has been an error saving your Debt
                      Consolidation Form.
                    </p>
                    <p>Please contact us to discuss this further.</p>
                  </>
                </AlertError>
              )}

              <div className="flex justify-end">
                <CTAButton
                  onClick={() => onSubmit()}
                  label="Save Consolidation"
                  loading={updateDebtConsolidationLoading}
                />
              </div>
            </form>
          )}
        </>
      </PageContent>
    </div>
  );
}
